<div class="auth-container">
  <div class="auth-background">
    <div class="gradient-orb gradient-orb-1"></div>
    <div class="gradient-orb gradient-orb-2"></div>
  </div>

  <div class="auth-content">
    <div class="auth-card">
      <!-- Top bar: Brand + Theme-Toggle -->
      <div class="desk-top">
        <div class="fp-brand">
          <img
            src="../../../../../assets/img/logo-text.svg"
            alt="FixPlaner"
            class="logo"
          />
        </div>
        <div class="auth-top-actions">
          <xds-icon-button
            [icon]="'fa-light ' + getThemeIconClass()"
            [ariaLabel]="'REG_THEME_TOGGLE_LABEL' | translate"
            (clicked)="toggleTheme()"
          ></xds-icon-button>
        </div>
      </div>

      <!-- Greeting — H1 + Lede (Eyebrow weg, Page-Zweck offensichtlich) -->
      <div class="desk-greet">
        <h1>
          {{ "RESET_PW_GREET_LINE_1" | translate }}
          <span class="grad">{{ "RESET_PW_GREET_BRAND" | translate }}</span>
        </h1>
        <p class="lede">{{ "RESET_PW_GREET_LEDE" | translate }}</p>
      </div>

      <!-- Form -->
      <form
        [formGroup]="resetPasswordForm"
        autocomplete="off"
        novalidate
        (ngSubmit)="resetPassword(resetPasswordForm.value)"
        class="auth-form xds-form-stack"
      >
        <!-- New Password -->
        <xds-form-field
          labelKey="NEW_PASSWORD"
          [required]="true"
          for="rp-password"
          hintKey="REG_PW_LABEL_HINT"
          hintIcon="fa-light fa-circle-info"
        >
          <xds-input
            id="rp-password"
            [type]="showPassword ? 'text' : 'password'"
            formControlName="password"
            [invalid]="validateControl('password')"
            placeholderKey="ENTER_NEW_PASSWORD"
            testId="reset-password-new"
            autocomplete="new-password"
          >
            <ng-container ngProjectAs="[xdsInputTrailing]">
              <xds-icon-button
                [icon]="showPassword ? 'fa-light fa-eye-slash' : 'fa-light fa-eye'"
                [ariaLabel]="(showPassword ? 'HIDE_PASSWORD' : 'SHOW_PASSWORD') | translate"
                (clicked)="toggleShowPassword()"
              ></xds-icon-button>
            </ng-container>
          </xds-input>
        </xds-form-field>

        <!-- Confirm Password -->
        <xds-form-field
          labelKey="CONFIRM_NEW_PASSWORD"
          [required]="true"
          for="rp-confirm"
          [invalid]="validateControl('confirm')"
          [errorKey]="hasError('confirm', 'required') ? 'CONFIRMATION IS REQUIRED' : 'PASSWORDS MUST MATCH'"
        >
          <xds-input
            id="rp-confirm"
            [type]="showConfirmPassword ? 'text' : 'password'"
            formControlName="confirm"
            [invalid]="validateControl('confirm')"
            placeholderKey="REG_PW_REPEAT_PLACEHOLDER"
            autocomplete="new-password"
          >
            <ng-container ngProjectAs="[xdsInputTrailing]">
              <xds-icon-button
                [icon]="showConfirmPassword ? 'fa-light fa-eye-slash' : 'fa-light fa-eye'"
                [ariaLabel]="(showConfirmPassword ? 'HIDE_PASSWORD' : 'SHOW_PASSWORD') | translate"
                (clicked)="toggleShowConfirmPassword()"
              ></xds-icon-button>
            </ng-container>
          </xds-input>
        </xds-form-field>

        <!-- Password strength block (Konzept A — kein Container, inline) -->
        <div
          class="pw-block"
          *ngIf="resetPasswordForm.get('password')?.value"
          id="rp-pw-block"
        >
          <div
            class="pw-bars"
            [attr.data-strength]="getPasswordStrengthData()"
            aria-hidden="true"
          >
            <div class="seg" [class.on]="passwordStrength >= 1"></div>
            <div class="seg" [class.on]="passwordStrength >= 2"></div>
            <div class="seg" [class.on]="passwordStrength >= 3"></div>
            <div class="seg" [class.on]="passwordStrength >= 4"></div>
            <div class="seg" [class.on]="passwordStrength >= 5"></div>
          </div>
          <div class="pw-line" id="rp-pw-line" aria-live="polite">
            <span class="pw-status">
              <span class="ico"><i class="fa-light fa-shield-halved"></i></span>
              <span>{{ "REG_PW_STRENGTH_LABEL" | translate }}</span>
            </span>
            <span class="strong" *ngIf="passwordStrength === 5">
              <span class="ico-ok"><i class="fa-solid fa-check"></i></span>
              {{ getPasswordStrengthLabel() | translate }} · {{ passwordStrength }} / 5
            </span>
            <span class="level" *ngIf="passwordStrength < 5">
              {{ getPasswordStrengthLabel() | translate }} · {{ passwordStrength }} / 5
            </span>
          </div>
          <div class="pw-checks">
            <span class="chk" [class.ok]="!hasError('password', 'minlength') && resetPasswordForm.get('password')?.value">
              <i class="fa-light fa-check"></i>{{ "REG_PW_CHK_LENGTH" | translate }}
            </span>
            <span class="chk" [class.ok]="!hasError('password', 'missingUpper') && resetPasswordForm.get('password')?.value">
              <i class="fa-light fa-check"></i>{{ "REG_PW_CHK_UPPER" | translate }}
            </span>
            <span class="chk" [class.ok]="!hasError('password', 'missingLower') && resetPasswordForm.get('password')?.value">
              <i class="fa-light fa-check"></i>{{ "REG_PW_CHK_LOWER" | translate }}
            </span>
            <span class="chk" [class.ok]="!hasError('password', 'missingDigit') && resetPasswordForm.get('password')?.value">
              <i class="fa-light fa-check"></i>{{ "REG_PW_CHK_DIGIT" | translate }}
            </span>
            <span class="chk" [class.ok]="!hasError('password', 'missingSpecial') && resetPasswordForm.get('password')?.value">
              <i class="fa-light fa-check"></i>{{ "REG_PW_CHK_SPECIAL" | translate }}
            </span>
          </div>
        </div>

        <!-- Error -->
        <!-- `errorMessage` trägt einen i18n-Schlüssel. Die Pipe statt `translate.instant()`,
             weil der Rückfall auf Englisch erst drei Sekunden nach dem Bootstrap greift —
             `instant()` liefert in diesem Fenster den Rohschlüssel, die Pipe zieht nach. -->
        <xds-banner *ngIf="showError" variant="danger">{{ errorMessage | translate }}</xds-banner>

        <!-- Actions -->
        <div class="desk-actions">
          <xds-button
            type="submit"
            variant="primary"
            iconRight="fa-light fa-check"
            [disabled]="!resetPasswordForm.valid"
            data-testid="reset-password-submit"
          >
            {{ "RESET_PASSWORD" | translate }}
          </xds-button>
          <p class="helper">
            <a [routerLink]="['/login']">
              <i class="fa-light fa-arrow-left"></i>
              {{ "BACK TO LOGIN" | translate }}
            </a>
          </p>
        </div>
      </form>
    </div>
  </div>
</div>
